(() => {
  if (window.__aoRealGalleryV47) return;
  window.__aoRealGalleryV47 = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches;

  function init(gallery) {
    if (!gallery || gallery.dataset.aoGalleryReady === 'true') return;

    const viewport = gallery.querySelector('[data-ao-real-gallery-viewport]');
    const track = gallery.querySelector('[data-ao-real-gallery-track]');
    const sourceSet = gallery.querySelector('[data-ao-real-gallery-set]');
    if (!viewport || !track || !sourceSet) return;

    gallery.dataset.aoGalleryReady = 'true';

    const sourceItems = [...sourceSet.querySelectorAll('[data-ao-real-gallery-item]')];
    const sourceImages = [...sourceSet.querySelectorAll('img[data-ao-real-source]')];

    /*
      The homepage uses one native scrolling set instead of three cloned sets.
      This keeps the same free-drag interaction while cutting image nodes and
      layout work by two thirds on the busiest landing page.
    */
    if (gallery.dataset.aoSimpleGallery === 'true') {
      gallery.classList.add('is-enhanced', 'is-loop-ready', 'is-simple');
      sourceItems.forEach((item) => item.classList.add('is-loaded', 'has-popped'));
      sourceImages.forEach((img, index) => {
        img.loading = index < 2 ? 'eager' : 'lazy';
        if ('fetchPriority' in img) img.fetchPriority = index < 2 ? 'high' : 'auto';
        if (img.complete && img.naturalWidth > 0) img.closest('[data-ao-real-gallery-item]')?.classList.add('is-loaded');
        else img.addEventListener('load', () => img.closest('[data-ao-real-gallery-item]')?.classList.add('is-loaded'), { once: true, passive: true });
      });

      if (!coarsePointer) {
        let dragging = false;
        let moved = false;
        let pointerId = null;
        let lastX = 0;

        viewport.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          dragging = true;
          moved = false;
          pointerId = event.pointerId;
          lastX = event.clientX;
          viewport.classList.add('is-dragging');
          try { viewport.setPointerCapture(pointerId); } catch (_) {}
        });
        viewport.addEventListener('pointermove', (event) => {
          if (!dragging || event.pointerId !== pointerId) return;
          const dx = event.clientX - lastX;
          if (Math.abs(dx) > 1) {
            viewport.scrollLeft -= dx;
            moved = true;
          }
          lastX = event.clientX;
          if (event.cancelable) event.preventDefault();
        }, { passive: false });
        const finishDrag = () => {
          dragging = false;
          viewport.classList.remove('is-dragging');
          try { if (pointerId !== null) viewport.releasePointerCapture(pointerId); } catch (_) {}
          pointerId = null;
        };
        viewport.addEventListener('pointerup', finishDrag);
        viewport.addEventListener('pointercancel', finishDrag);
        viewport.addEventListener('click', (event) => {
          if (!moved) return;
          if (event.target.closest('a')) {
            event.preventDefault();
            event.stopPropagation();
          }
          moved = false;
        }, true);
      }

      viewport.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        if (event.shiftKey || Math.abs(event.deltaY) < 45) {
          viewport.scrollLeft += event.deltaY;
          if (event.cancelable) event.preventDefault();
        }
      }, { passive: false });
      return;
    }

    /*
      Stable infinite loop for iOS:
      - the DOM never moves while the user/momentum scroll is active;
      - two static copies are created once at boot;
      - recentering happens only after scrolling has fully settled;
      - Safari toolbar height changes never reset the position.
    */
    sourceSet.dataset.aoLoopCopy = 'main';
    const beforeSet = sourceSet.cloneNode(true);
    const afterSet = sourceSet.cloneNode(true);
    beforeSet.dataset.aoLoopCopy = 'before';
    afterSet.dataset.aoLoopCopy = 'after';

    /*
      Homepage performance mode: keep the exact same infinite-loop mechanics,
      native touch scrolling and pop animations, but clone only two edge
      clusters instead of cloning the whole 35-photo set twice. On mobile this
      cuts the gallery from 105 image nodes to 55. The Realizacie page does not
      use this flag and therefore stays byte-for-byte on the original full-loop
      behavior the user already likes.
    */
    if (gallery.dataset.aoLightLoop === 'true') {
      const beforeClusters = [...beforeSet.querySelectorAll('[data-ao-real-gallery-cluster]')];
      const afterClusters = [...afterSet.querySelectorAll('[data-ao-real-gallery-cluster]')];
      const keepCount = Math.min(2, Math.max(1, beforeClusters.length));
      beforeClusters.slice(0, Math.max(0, beforeClusters.length - keepCount)).forEach((cluster) => cluster.remove());
      afterClusters.slice(keepCount).forEach((cluster) => cluster.remove());
      gallery.classList.add('is-light-loop');
    }
    beforeSet.setAttribute('aria-hidden', 'true');
    afterSet.setAttribute('aria-hidden', 'true');

    [beforeSet, afterSet].forEach((copy) => {
      copy.querySelectorAll('img').forEach((img) => {
        img.alt = '';
        img.loading = 'lazy';
        img.fetchPriority = 'low';
        img.removeAttribute('data-ao-real-source');
      });
      copy.querySelectorAll('[data-ao-real-gallery-item]').forEach((item) => {
        item.classList.add('is-loaded', 'has-popped', 'is-loop-copy');
      });
    });

    track.insertBefore(beforeSet, sourceSet);
    track.appendChild(afterSet);

    sourceItems.forEach((item, index) => {
      item.classList.add('is-pop-pending');
      item.style.setProperty('--ao-pop-delay', `${(index % 5) * 34}ms`);
    });

    let popObserver = null;
    const finishPop = (item) => {
      if (!item || item.classList.contains('has-popped')) return;
      item.classList.add('has-popped');
      item.removeAttribute('data-ao-pop-waiting');
      popObserver?.unobserve(item);
      window.setTimeout(() => item.classList.remove('is-pop-pending'), 760);
    };

    const markLoaded = (img) => {
      const item = img.closest('[data-ao-real-gallery-item]');
      if (!item) return;
      item.classList.add('is-loaded');
      if (item.dataset.aoPopWaiting === 'true') finishPop(item);
    };

    sourceImages.forEach((img, index) => {
      if ('fetchPriority' in img) img.fetchPriority = index < 2 ? 'high' : 'auto';
      if (img.complete && img.naturalWidth > 0) markLoaded(img);
      else img.addEventListener('load', () => markLoaded(img), { once: true, passive: true });
    });

    if (reduceMotion || !('IntersectionObserver' in window)) {
      sourceItems.forEach((item) => finishPop(item));
    } else {
      popObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.08) return;
          const item = entry.target;
          if (item.classList.contains('is-loaded')) finishPop(item);
          else item.dataset.aoPopWaiting = 'true';
        });
      }, {
        root: viewport,
        rootMargin: '0px 16% 0px 16%',
        threshold: [0.08, 0.2]
      });
      sourceItems.forEach((item) => popObserver.observe(item));
    }

    // Warm decoded bitmaps gradually with at most two decodes in flight.
    // Lazy source images begin network work only as they approach the viewport.
    const decodeQueue = sourceImages.slice();
    let activeDecodes = 0;
    const pumpDecodeQueue = () => {
      while (activeDecodes < 2 && decodeQueue.length) {
        const img = decodeQueue.shift();
        if (!img) break;
        activeDecodes += 1;
        const run = () => {
          const task = typeof img.decode === 'function' ? img.decode() : Promise.resolve();
          Promise.resolve(task).catch(() => {}).finally(() => {
            activeDecodes -= 1;
            if (decodeQueue.length) {
              if ('requestIdleCallback' in window) requestIdleCallback(pumpDecodeQueue, { timeout: 700 });
              else setTimeout(pumpDecodeQueue, 40);
            }
          });
        };
        if (img.complete) run();
        else {
          img.addEventListener('load', run, { once: true, passive: true });
          img.addEventListener('error', () => {
            activeDecodes -= 1;
            if (decodeQueue.length) {
              if ('requestIdleCallback' in window) requestIdleCallback(pumpDecodeQueue, { timeout: 700 });
              else setTimeout(pumpDecodeQueue, 40);
            }
          }, { once: true, passive: true });
        }
      }
    };
    if ('requestIdleCallback' in window) requestIdleCallback(pumpDecodeQueue, { timeout: 900 });
    else setTimeout(pumpDecodeQueue, 180);

    const viewportPaddingLeft = () => Number.parseFloat(getComputedStyle(viewport).paddingLeft) || 0;
    const mainStart = () => sourceSet.offsetLeft - viewportPaddingLeft();
    const cycleWidth = () => afterSet.offsetLeft - sourceSet.offsetLeft;

    let ready = false;
    let recentring = false;
    let scrollTimer = 0;
    let lastViewportWidth = viewport.clientWidth;

    const jumpTo = (x) => {
      recentring = true;
      const previousBehavior = viewport.style.scrollBehavior;
      viewport.style.scrollBehavior = 'auto';
      viewport.scrollLeft = x;
      viewport.style.scrollBehavior = previousBehavior;
      requestAnimationFrame(() => { recentring = false; });
    };

    const seedMiddle = () => {
      const start = mainStart();
      const cycle = cycleWidth();
      if (cycle <= 10) return false;
      jumpTo(start);
      ready = true;
      gallery.classList.add('is-enhanced', 'is-loop-ready');
      return true;
    };

    const recenterAfterSettle = () => {
      if (!ready || recentring) return;
      const start = mainStart();
      const cycle = cycleWidth();
      if (cycle <= 10) return;

      const x = viewport.scrollLeft;
      let target = x;
      if (x < start) target = x + cycle;
      else if (x >= start + cycle) target = x - cycle;

      if (Math.abs(target - x) > 1) jumpTo(target);
    };

    const scheduleSettle = () => {
      clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(recenterAfterSettle, 150);
    };

    viewport.addEventListener('scroll', () => {
      if (!recentring) scheduleSettle();
    }, { passive: true });

    if ('onscrollend' in viewport) {
      viewport.addEventListener('scrollend', recenterAfterSettle, { passive: true });
    }

    // Desktop mouse drag only. Touch devices use native iOS/Android momentum scrolling.
    if (!coarsePointer) {
      let dragging = false;
      let moved = false;
      let pointerId = null;
      let lastX = 0;

      const begin = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        dragging = true;
        moved = false;
        pointerId = event.pointerId;
        lastX = event.clientX;
        viewport.classList.add('is-dragging');
        try { viewport.setPointerCapture(pointerId); } catch (_) {}
      };

      const move = (event) => {
        if (!dragging || (pointerId !== null && event.pointerId !== pointerId)) return;
        const dx = event.clientX - lastX;
        if (Math.abs(dx) > 1) {
          viewport.scrollLeft -= dx;
          moved = true;
        }
        lastX = event.clientX;
        if (event.cancelable) event.preventDefault();
      };

      const end = () => {
        if (!dragging) return;
        dragging = false;
        viewport.classList.remove('is-dragging');
        try { if (pointerId !== null) viewport.releasePointerCapture(pointerId); } catch (_) {}
        pointerId = null;
        scheduleSettle();
      };

      viewport.addEventListener('pointerdown', begin);
      viewport.addEventListener('pointermove', move, { passive: false });
      viewport.addEventListener('pointerup', end);
      viewport.addEventListener('pointercancel', end);
      viewport.addEventListener('click', (event) => {
        if (!moved) return;
        if (event.target.closest('a')) {
          event.preventDefault();
          event.stopPropagation();
        }
        moved = false;
      }, true);
    }

    viewport.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (event.shiftKey || Math.abs(event.deltaY) < 45) {
        viewport.scrollLeft += event.deltaY;
        scheduleSettle();
        if (event.cancelable) event.preventDefault();
      }
    }, { passive: false });

    // ResizeObserver reacts only to a real WIDTH change (orientation/window resize).
    // Safari showing/hiding its bars changes height and must not reset the carousel.
    let resizeTimer = 0;
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(() => {
      const width = viewport.clientWidth;
      if (Math.abs(width - lastViewportWidth) < 8) return;
      const oldStart = mainStart();
      const oldCycle = cycleWidth();
      const relative = oldCycle > 10 ? (viewport.scrollLeft - oldStart) / oldCycle : 0;
      lastViewportWidth = width;
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        const newStart = mainStart();
        const newCycle = cycleWidth();
        if (newCycle > 10) jumpTo(newStart + relative * newCycle);
      }, 120);
    }) : null;
    resizeObserver?.observe(viewport);

    // Seed immediately when layout is measurable; fall back to one/two frames only if needed.
    // This avoids showing the leading loop copy for a visible frame.
    if (!seedMiddle()) {
      requestAnimationFrame(() => {
        if (!seedMiddle()) requestAnimationFrame(() => { if (!seedMiddle()) setTimeout(seedMiddle, 80); });
      });
    }

    gallery.addEventListener('shopify:section:unload', () => {
      clearTimeout(scrollTimer);
      clearTimeout(resizeTimer);
      popObserver?.disconnect();
      resizeObserver?.disconnect();
    }, { once: true });
  }

  const boot = (scope = document) => scope.querySelectorAll('[data-ao-real-gallery]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
